import { Button, InputText, Loading } from '@/components';
import React from 'react';
import { Container } from 'reactstrap';
import { FormData, FormErrors, OnChangeFormInput } from '@/hooks';
import { States } from '@/helpers/common/states';
import { ChargeBack } from '@/model/ChargeBack';
import dayjs from 'dayjs';
import { toCurrency } from '@/helpers/masks/toCurrency';
import { CustomTable } from '@/components/Table';
import { FormInputName, ticketColumns, transferColumns } from '../../types';
import './styles.scss';

interface StateProps {
  state: States;
  formData: FormData;
  formErrors: FormErrors;
  chargeBack?: ChargeBack;
}

interface DispatchProps {
  onChangeFormInput: OnChangeFormInput;
  onFilter: () => void;
  onExport: () => void;
  onCancelOrder: () => void;
}

type Props = StateProps & DispatchProps;

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="chargeback-field">
    <dt>{label}</dt>
    <dd>{children || 'Não informado'}</dd>
  </div>
);

const formatDate = (value?: string | Date | null, precise = false): string =>
  value && dayjs(value).isValid()
    ? dayjs(value).format(precise ? 'DD/MM/YYYY [às] HH:mm:ss.SSS' : 'DD/MM/YYYY [às] HH:mm')
    : 'Não informado';

const maskCPF = (value?: string | null): string => {
  const digits = value?.replace(/\D/g, '');
  return digits?.length === 11
    ? `${digits.slice(0, 3)}.***.***-${digits.slice(-2)}`
    : 'Não informado';
};

const ticketStatus = (status: number): string =>
  ['Ativo', 'Inativo', 'Excluído', 'Transferido'][status] || 'Não informado';

export const ChargebackContainer: React.FC<Props> = ({
  state,
  formData,
  onChangeFormInput,
  formErrors,
  onFilter,
  chargeBack,
  onExport,
  onCancelOrder,
}): JSX.Element => {
  const loading = state === States.loading;
  const order = chargeBack?.order;
  const payment = chargeBack?.pagseguro;
  const validation = chargeBack?.cardValidation;
  const tickets = order?.itens || [];
  const transfers = chargeBack?.transfers || [];

  return (
    <>
      <Loading isVisible={loading} />
      <Container className="mainContainer chargeback-page" fluid={true}>
        <header className="chargeback-heading">
          <div>
            <span className="chargeback-eyebrow">Suporte</span>
            <h1>Consulta de chargeback</h1>
            <p>Localize a transação e confira os dados da compra antes de tomar uma ação.</p>
          </div>
        </header>
        <section className="chargeback-panel chargeback-search" aria-label="Buscar transação">
          <form
            onSubmit={event => {
              event.preventDefault();
              if (!loading) onFilter();
            }}
          >
            <InputText
              name="transactionCode"
              label="Código da transação PagSeguro"
              placeholder="Digite ou cole o código da transação"
              value={formData[FormInputName.transactionCode]}
              onChange={event =>
                onChangeFormInput(FormInputName.transactionCode)(event.target.value)
              }
              error={formErrors.transactionCode?.[0]}
              disabled={loading}
            />
            <Button
              type="submit"
              title={loading ? 'Consultando…' : 'Consultar transação'}
              disabled={loading}
              onClick={() => undefined}
            />
          </form>
          <p>
            Use o código da cobrança, com ou sem hífens. Este campo não busca pelo número do pedido.
          </p>
        </section>

        {!order && !loading && (
          <div className="chargeback-empty">
            <h2>Consulte uma transação</h2>
            <p>Os dados do pedido, pagamento e ingressos serão exibidos aqui.</p>
          </div>
        )}

        {order && payment && (
          <>
            <div className="chargeback-result-heading">
              <h2>Detalhes da transação</h2>
              <Button
                title="Exportar relatório em PDF"
                theme="outlineDark"
                onClick={onExport}
                disabled={loading}
              />
            </div>
            <div id="ExportToPdf" className="chargeback-report">
              <section className="chargeback-panel">
                <div className="chargeback-section-heading">
                  <h2>Resumo do pedido</h2>
                  <span className="chargeback-tag">{tickets.length} ingresso(s)</span>
                </div>
                <dl className="chargeback-summary">
                  <Field label="Valor da compra">
                    <span className="chargeback-amount">
                      {order.totalValue == null ? 'Não informado' : toCurrency(order.totalValue)}
                    </span>
                  </Field>
                  <Field label="Comprador">{order.client?.clientName}</Field>
                  <Field label="Data da compra">{formatDate(order.date)}</Field>
                </dl>
                <dl className="chargeback-fields">
                  <Field label="Código do pedido">{order.id}</Field>
                  <Field label="CPF do comprador">{maskCPF(order.client?.clientCpf)}</Field>
                </dl>
              </section>

              <div className="chargeback-columns">
                <section className="chargeback-panel">
                  <h2>Pagamento · PagSeguro</h2>
                  <dl className="chargeback-fields">
                    <Field label="Código do pedido PagSeguro">{payment.pagseguroOrderId}</Field>
                    <Field label="Código da transação">{payment.pagseguroChargeId}</Field>
                    <Field label="Bandeira">{payment.cardBrand}</Field>
                    <Field label="Cartão">
                      {payment.cardFirstDigits && payment.cardLastDigits
                        ? `${payment.cardFirstDigits.slice(0, 4)} ${payment.cardFirstDigits.slice(
                            4,
                            6,
                          )}** **** ${payment.cardLastDigits}`
                        : 'Não informado'}
                    </Field>
                    <Field label="Titular do cartão">{payment.cardHolder}</Field>
                    <Field label="CPF do titular">{maskCPF(payment.cardHolderCpf)}</Field>
                    <Field label="Autenticação 3DS">{payment.authenticationId}</Field>
                  </dl>
                </section>
                <section className="chargeback-panel">
                  <div className="chargeback-section-heading">
                    <h2>Confirmação do cartão</h2>
                  </div>
                  <p className="chargeback-description">
                    Verificação do valor cobrado na fatura pelo titular do cartão.
                  </p>
                  {validation?.validateOrder ? (
                    <>
                      <span
                        className={`chargeback-tag ${
                          validation.validated ? 'chargeback-tag-success' : ''
                        }`}
                      >
                        {validation.validated ? 'Valor confirmado' : 'Valor não confirmado'}
                      </span>
                      <dl className="chargeback-fields">
                        <Field label="Transação de verificação">{validation.validateOrder}</Field>
                        <Field label="Valor cobrado">
                          {validation.validateValue == null
                            ? 'Não informado'
                            : toCurrency(validation.validateValue)}
                        </Field>
                        <Field label="Data da cobrança">
                          {formatDate(validation.validateCreateDate, true)}
                        </Field>
                        <Field label="Data da confirmação">
                          {formatDate(validation.validateDate, true)}
                        </Field>
                      </dl>
                    </>
                  ) : (
                    <p className="chargeback-note">
                      Não há confirmação de valor registrada para esta consulta.
                    </p>
                  )}
                </section>
              </div>

              <section className="chargeback-panel">
                <h2>Ingressos do pedido</h2>
                <div className="chargeback-table">
                  <CustomTable
                    columns={ticketColumns}
                    data={tickets.map(ticket => ({
                      event: ticket.event,
                      section: ticket.section,
                      ticket: ticket.ticket,
                      status: ticketStatus(ticket.statusType),
                      used: ticket.validatedDate
                        ? formatDate(ticket.validatedDate)
                        : 'Não utilizado',
                    }))}
                    numberRowsPerPage={Math.max(tickets.length, 1)}
                    progressPending={false}
                  />
                </div>
              </section>

              <section className="chargeback-panel">
                <div className="chargeback-section-heading">
                  <h2>Transferências de ingressos</h2>
                  <span className="chargeback-tag">{transfers.length} transferência(s)</span>
                </div>
                {transfers.length === 0 && (
                  <p className="chargeback-note">
                    Nenhuma transferência registrada para este pedido.
                  </p>
                )}
                {transfers.map(transfer => (
                  <div className="chargeback-transfer" key={transfer.id}>
                    <dl className="chargeback-summary">
                      <Field label="Recebedor">{transfer.client?.clientName}</Field>
                      <Field label="CPF">{maskCPF(transfer.client?.clientCpf)}</Field>
                      <Field label="Data da transferência">{formatDate(transfer.date)}</Field>
                    </dl>
                    <div className="chargeback-table">
                      <CustomTable
                        columns={transferColumns}
                        data={(transfer.itens || []).map(ticket => ({
                          event: ticket.event,
                          section: ticket.section,
                          ticket: ticket.ticket,
                          used: ticket.validatedDate
                            ? formatDate(ticket.validatedDate)
                            : 'Não utilizado',
                        }))}
                        numberRowsPerPage={Math.max(transfer.itens?.length || 0, 1)}
                        progressPending={false}
                      />
                    </div>
                  </div>
                ))}
              </section>
            </div>
            <section className="chargeback-danger" aria-label="Cancelamento do pedido">
              <div>
                <h2>Cancelar pedido e sinalizar fraude</h2>
                <p>
                  Esta ação cancela o pedido sem estorno e marca comprador e recebedor do ingresso
                  com alerta de fraude.
                </p>
              </div>
              <Button
                title="Cancelar pedido e informar fraude"
                theme="red"
                onClick={onCancelOrder}
                disabled={loading || !order.id}
              />
            </section>
          </>
        )}
      </Container>
    </>
  );
};
